import React, { useState, useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { Space } from "antd";
import TableField from "components/molecules/TableField";
import { useLocation } from "react-router-dom";
import { coverageOptions, yesOrNoOptions } from "../utils/options";
import { pharmacyTypesMap } from "components/pages/AddDrugsPage/utils/steps";
import TableMenu from "components/molecules/TableMenu";

const columns = (title, type) =>
  [
    {
      title: "",
      dataIndex: "idx",
      width: 22.5,
      editable: false,
      fixed: "left",
    },
    {
      title: "Label",
      dataIndex: "label",
      width: 100,
      editable: true,
      disabled: true,
      inputType: "text",
      shouldCellUpdate: () => false,
    },
    {
      title: "",
      dataIndex: "tiers",
      inputType: "tiers",
      width: 120,
      editable: true,
      shouldCellUpdate: (record, prevRecord) => {
        const prevVal = prevRecord.tiers;
        const currVal = record.tiers;
        if (Array.isArray(prevVal) && Array.isArray(currVal)) {
          if (prevVal.length !== currVal.length) return true;
          return prevVal.some((v, i) => v !== currVal[i]);
        }
        return prevVal !== currVal;
      },
    },
    {
      title: "You Have to Pay",
      dataIndex: "drugsCoverageType",
      width: 100,
      dropdownMatchSelectWidth: 300,
      editable: true,
      inputType: "drugsCoverageType",
      pharmacyType: type,
      options: coverageOptions,
      shouldCellUpdate: () => false,
    },
    {
      title: "Coinsurance Rate",
      dataIndex: "drugsCoinsuranceRate",
      width: 70,
      editable: true,
      inputType: "drugsCoinsuranceRate",
      pharmacyType: type,
      shouldCellUpdate: (record, prevRecord) => {
        return record.tiers.some(
          (tier) => record[type][tier]?.drugsCoverageType !== prevRecord[type][tier]?.drugsCoverageType,
        );
      },
    },
    {
      title: `How Much is the Copay for ${title}`,
      dataIndex: "drugsCopay",
      width: 120,
      editable: true,
      inputType: "drugsCopay",
      pharmacyType: type,
      shouldCellUpdate: (record, prevRecord) => {
        return record.tiers.some(
          (tier) => record[type][tier]?.drugsCoverageType !== prevRecord[type][tier]?.drugsCoverageType,
        );
      },
    },
    {
      title: "Subject to Deductible",
      dataIndex: "subjToDeductible",
      width: 70,
      editable: true,
      inputType: "subjToDeductible",
      pharmacyType: type,
      options: yesOrNoOptions,
      shouldCellUpdate: () => false,
    },
    {
      title: "",
      key: "operation",
      fixed: "right",
      width: 48,
      render: (_, record) => record.idx > 1 && <TableMenu record={record} step={5} pharmacyType={type} />,
    },
  ].map((col) => {
    return {
      ...col,
      onCell: (_, idx) => ({
        index: idx,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        inputType: col.inputType,
        options: col.options,
        dropdownMatchSelectWidth: col.dropdownMatchSelectWidth,
        disabled: col.disabled || false,
        pharmacyType: col.pharmacyType,
        emptyValue: col.emptyValue,
      }),
    };
  });

const hideTableRows = (table, pharmacyManagersTable, type) => {
  return table.map((plan) => {
    const pbm = pharmacyManagersTable.find((pbm) => pbm.planList.includes(plan.label));
    return {
      ...plan,
      shouldHide: pbm ? !pbm.pharmacyTypes.map((pharmacyType) => pharmacyTypesMap[pharmacyType]).includes(type) : false,
    };
  });
};

const Step = ({ title, type }) => {
  const { state } = useLocation();
  const { values, setErrors, setTouched, setFieldValue } = useFormikContext();
  const { table, pharmacyManagersTable } = values;
  const syncedRef = useRef(false);

  useEffect(() => {
    document.getElementById("form-content").scrollTo({ top: 0, behavior: "smooth" });
    setErrors({});
    setTouched({}, false);

    if (syncedRef.current) return;
    syncedRef.current = true;

    const newTable = table.map((plan) => {
      const mgr = pharmacyManagersTable.find((m) => m.planList.includes(plan.label));
      if (!mgr) return plan;
      return {
        ...plan,
        tiers: Array.from({ length: mgr.numberOfTiers }, (_, i) => `${mgr.tierCodes[i]} - ${mgr.tierNames[i]}`),
      };
    });
    setFieldValue("table", newTable);
  }, []);

  const filterColumns = (table, columns) => {
    const hasCoinsurance = table.some((row) =>
      Object.keys(row[type]).some((tier) => {
        return row[type][tier].drugsCoverageType === "coinsurance";
      }),
    );
    const hasCopay = table.some((row) =>
      Object.keys(row[type]).some((tier) => {
        return row[type][tier].drugsCoverageType === "copay";
      }),
    );

    columns = columns.filter((col) => {
      if (col.dataIndex === "drugsCoinsuranceRate") return hasCoinsurance;
      if (col.dataIndex === "drugsCopay") return hasCopay;
      return true;
    });
    return columns;
  };

  const filteredColumns = filterColumns(table, columns(title, type));
  const tableWithHiddenProps = hideTableRows(table, pharmacyManagersTable, type);

  return (
    <>
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        <TableField name="table" columns={filteredColumns} dataSource={tableWithHiddenProps} tableLayout="fixed" />
      </Space>
    </>
  );
};

export default Step;
